import { Download } from "@mui/icons-material";
import { Input, Select, Option, IconButton, Stack, Tooltip, Box } from "@mui/joy";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useShallow } from "zustand/react/shallow";

import api from "@/core/api";
import { useLeagueQuery } from "@/hooks/competitions";
import downloadBlob from "@/utils/downloadBlob";

import { useLeagueEntriesTableContext } from "./LeagueEntriesContext";

const CompetitorSearch = (_props: { leagueUuid: string }) => {
  const { competitorSearchQuery, setCompetitorSearchQuery } = useLeagueEntriesTableContext();

  const [query, setQuery] = useState(competitorSearchQuery);
  const debouncedUpdate = useDebouncedCallback((q) => {
    setCompetitorSearchQuery(q);
  }, 150);

  return (
    <Input
      placeholder="Search competitors..."
      value={query}
      onChange={(e) => {
        setQuery(e.target.value);
        if (e.target.value === "") {
          setCompetitorSearchQuery("");
        } else {
          debouncedUpdate(e.target.value);
        }
      }}
    />
  );
};

const CategoriesSelect = ({ leagueUuid }: { leagueUuid: string }) => {
  const { data: league } = useLeagueQuery(leagueUuid);
  const [selectedCategoryUuid, setSelectedCategoryUuid] = useLeagueEntriesTableContext(
    useShallow((state) => [state.selectedCategoryUuid, state.setSelectedCategoryUuid]),
  );

  return (
    <Select<string>
      placeholder="Select category"
      value={selectedCategoryUuid || "ALL"}
      onChange={(_e, value) => {
        if (value === "ALL") {
          setSelectedCategoryUuid(null);
          return;
        }
        if (!value) {
          return;
        }
        setSelectedCategoryUuid(value);
      }}
    >
      <Option value="ALL">All categories</Option>
      {league?.categories.map((category) => (
        <Option key={category.uuid} value={category.uuid}>
          {category.name}
        </Option>
      ))}
    </Select>
  );
};

const LeagueEntriesToolbar = ({ leagueUuid }: { leagueUuid: string }) => {
  const { data: league } = useLeagueQuery(leagueUuid);

  const downloadXlsx = async () => {
    const response = await api.competitions.getLeagueLeaderboardXls(leagueUuid);
    if (!response.ok) {
      throw new Error("Failed to download Excel file");
    }
    downloadBlob(await response.blob(), `${league ? league.name + " " : ""}leaderboard.xlsx`);
  };

  const downloadXlsxBlobMutation = useMutation({
    mutationFn: downloadXlsx,
  });

  return (
    <Stack
      direction="row"
      sx={{
        padding: 1,
        gap: 1,
        borderBottom: "1px solid",
        borderColor: "neutral.200",
      }}
    >
      <CategoriesSelect leagueUuid={leagueUuid} />
      <CompetitorSearch leagueUuid={leagueUuid} />
      <Box sx={{ flex: 1 }}></Box>

      <Tooltip title="Download leaderboard as Excel spreadsheet">
        <IconButton
          onClick={() => downloadXlsxBlobMutation.mutate()}
          disabled={downloadXlsxBlobMutation.isPending}
          loading={downloadXlsxBlobMutation.isPending}
        >
          <Download />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

export default LeagueEntriesToolbar;
